import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, ArrowRight, Building2, Globe, TrendingDown, Shield, Zap, Award } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_circularity-hub/artifacts/hwzjkab2_DecarbonIQ%20%2814%29.png";

const plans = [
  {
    key: "starter",
    name: "Starter",
    price: "Free",
    period: "forever",
    description: "Begin your climate measurement journey",
    color: "border-slate-200",
    badge: null,
    features: [
      "1 organization profile",
      "2 assessments per year",
      "Basic climate scorecard",
      "Global emission factors",
      "Evidence upload",
      "Community support",
    ],
    excluded: ["Registry publication", "AI insights", "PDF export", "Verified badge"],
    cta: "Get Started Free",
    ctaStyle: "border border-slate-300 text-slate-700 hover:bg-slate-50",
    ctaRoute: "/get-started?role=sme",
    trackingParams: { cta_source: "pricing_page", plan_selected: "starter", role: "sme" },
    highlight: false,
  },
  {
    key: "sme_verified",
    name: "SME Verified",
    price: "$49",
    period: "/month",
    description: "Full credentialing for small businesses",
    color: "border-[#059669]",
    badge: "Most Popular",
    features: [
      "Unlimited assessments",
      "Climate Passport + Public Registry",
      "AI-powered executive insights",
      "Boardroom-quality PDF exports",
      "97-factor Africa factor library",
      "Evidence vault + verification",
      "Platform Verified badge for website",
      "5 team members",
      "Priority support",
    ],
    excluded: [],
    cta: "Start Free Trial",
    ctaStyle: "bg-[#059669] text-white hover:bg-emerald-700",
    ctaRoute: "/signup?role=sme&plan=verified",
    trackingParams: { cta_source: "pricing_page", plan_selected: "sme_verified", role: "sme" },
    highlight: true,
  },
  {
    key: "corporate",
    name: "Corporate",
    price: "Custom",
    period: "",
    description: "Multi-site decarbonization & reporting platform",
    color: "border-[#0F172A]",
    badge: null,
    features: [
      "Multi-site emissions aggregation (Scope 1/2/3)",
      "Decarbonization portfolio & program builder",
      "Corporate → FI financing pipeline",
      "Executive reports + board-ready PDFs",
      "NDC & Article 6 alignment context",
      "Unlimited team + API access",
      "Dedicated onboarding + support",
    ],
    excluded: [],
    cta: "Contact Sales",
    ctaStyle: "bg-[#0F172A] text-white hover:bg-slate-800",
    ctaRoute: "/academy?mode=enterprise&track=corporate",
    trackingParams: { cta_source: "pricing_page", plan_selected: "corporate", role: "corporate" },
    customPricing: true,
    customNote: [
      "Custom pricing based on portfolio size",
      "Includes onboarding + support",
      "Designed for large industrial groups",
    ],
    highlight: false,
  },
  {
    key: "financial",
    name: "Financial Institution",
    price: "Custom",
    period: "",
    description: "Climate finance origination & portfolio intelligence",
    color: "border-slate-300",
    badge: "Enterprise",
    features: [
      "Full FI workspace (Client & Deals / Internal Portfolio)",
      "Deal Signal triage across SME, Project, and Corporate",
      "Climate risk + scenario analysis (Net Zero, 2°C, Delayed)",
      "Financing structuring (Green Loan, SLL, Project Finance)",
      "Carbon structuring + VCM/Article 6 readiness",
      "Portfolio exposure + TCFD alignment",
      "White-label reporting option",
      "SLA + dedicated analyst support",
    ],
    excluded: [],
    cta: "Contact Sales",
    ctaStyle: "bg-[#0F172A] text-white hover:bg-slate-800",
    ctaRoute: "/academy?mode=enterprise&track=fi",
    trackingParams: { cta_source: "pricing_page", plan_selected: "fi", role: "fi" },
    customPricing: true,
    customNote: [
      "Custom pricing based on portfolio size",
      "Includes onboarding + support",
      "Designed for institutions",
    ],
    highlight: false,
  },
  {
    key: "enterprise",
    name: "Enterprise",
    price: "Custom",
    period: "",
    description: "Tailored for governments, development banks, and large corporates",
    color: "border-violet-300",
    badge: "Custom",
    features: [
      "Everything in Financial Institution",
      "Custom factor library extensions",
      "Dedicated methodology governance",
      "On-site implementation support",
      "Custom SLA and uptime guarantee",
      "Data residency options",
      "Volume licensing for partner networks",
    ],
    excluded: [],
    cta: "Talk to Sales",
    ctaStyle: "border border-violet-300 text-violet-700 hover:bg-violet-50",
    ctaRoute: "/academy?mode=enterprise&type=enterprise",
    trackingParams: { cta_source: "pricing_page", plan_selected: "enterprise", role: "enterprise" },
    customPricing: true,
    customNote: [
      "Custom pricing based on program scope",
      "Includes implementation + governance support",
      "Designed for governments and DFIs",
    ],
    isEnterprise: true,
    highlight: false,
  },
];

const faq = [
  {
    q: "Is the data confidential?",
    a: "Yes. Your assessment data is private by default. Only data you explicitly choose to publish to the public registry is visible externally."
  },
  {
    q: "What emission factors are included?",
    a: "97 approved emission factors covering Africa-specific electricity grids (22 countries), fuel, transport, waste, materials, and refrigerants — all sourced from IEA, IPCC, DEFRA, and regional regulators."
  },
  {
    q: "What does 'Platform Verified' mean?",
    a: "DeCarbonIQ reviews the methodology, data inputs, and calculation approach. A Platform Verified credential carries more weight with stakeholders than self-reported data alone."
  },
  {
    q: "Can I upgrade or downgrade my plan?",
    a: "Yes. Upgrades are immediate. Downgrades take effect at the end of your billing period."
  },
  {
    q: "Is this recognized by DFIs or investors?",
    a: "DeCarbonIQ reports are aligned with GHG Protocol Corporate Standard. For formal DFI submissions, we recommend upgrading to our Financial Institution plan for DFI-ready report templates."
  },
];

export default function Pricing() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [billingAnnual, setBillingAnnual] = useState(false);

  const discount = billingAnnual ? 0.8 : 1;

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Nav */}
      <nav className="border-b border-slate-200 sticky top-0 z-40 bg-white">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/">
            <div className="bg-[#0F172A] rounded-lg px-3 py-1.5">
              <img src={LOGO_URL} alt="DeCarbonIQ" className="h-7 w-auto" style={{ filter: "brightness(0) invert(1)" }} />
            </div>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/login" className="text-sm text-slate-500 hover:text-slate-800">Sign in</Link>
            <Link to="/signup" className="px-4 py-2 bg-[#0F172A] text-white text-sm font-medium rounded-lg hover:bg-[#1E293B]">
              Get Started
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-16 pb-12 px-6 bg-[#F8FAFC] border-b border-slate-200">
        <div className="text-center">
          {/* Social proof */}
          <div className="inline-flex items-center gap-2 bg-[#0F172A] text-white text-[11px] font-semibold px-4 py-2 rounded-full mb-5 tracking-wide">
            <span className="w-1.5 h-1.5 rounded-full bg-[#059669] animate-pulse" />
            Used by SMEs, Corporates, and Financial Institutions across Africa
          </div>
          <p className="text-xs font-semibold uppercase tracking-widest text-[#059669] mb-3">Transparent Pricing</p>
          <h1 className="text-4xl sm:text-5xl font-semibold text-[#0F172A] tracking-tight mb-4">
            Climate credibility at every scale.
          </h1>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto mb-8 leading-relaxed">
            From first measurement to institutional-grade verification. No hidden fees. No complex contracts. Start free, upgrade as you grow.
          </p>
          {/* Billing toggle */}
          <div className="inline-flex items-center gap-3 p-1 bg-white border border-slate-200 rounded-lg">
            <button onClick={() => setBillingAnnual(false)}
              className={`px-4 py-1.5 rounded text-sm font-medium transition-colors ${!billingAnnual ? "bg-[#0F172A] text-white" : "text-slate-500"}`}>
              Monthly
            </button>
            <button onClick={() => setBillingAnnual(true)}
              className={`px-4 py-1.5 rounded text-sm font-medium transition-colors ${billingAnnual ? "bg-[#0F172A] text-white" : "text-slate-500"}`}>
              Annual <span className="text-[#059669] text-xs ml-1">Save 20%</span>
            </button>
          </div>
        </div>
      </section>

      {/* Plans */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5" data-testid="pricing-plans">
            {plans.map(plan => (
              <div key={plan.key} className={`relative bg-white border-2 ${plan.color} rounded-xl p-6 flex flex-col ${plan.highlight ? "shadow-lg" : ""}`}
                   data-testid={`plan-${plan.key}`}>
                {plan.badge && (
                  <div className={`absolute -top-3 left-6 px-3 py-1 text-xs font-bold rounded-full ${plan.highlight ? "bg-[#059669] text-white" : "bg-[#0F172A] text-white"}`}>
                    {plan.badge}
                  </div>
                )}
                <div className="mb-5">
                  <h3 className="text-lg font-bold text-[#0F172A] mb-1">{plan.name}</h3>
                  <p className="text-xs text-slate-400 mb-4">{plan.description}</p>
                  <div className="flex items-end gap-1 mb-1">
                    {plan.customPricing ? (
                      <div>
                        <span className="text-3xl font-bold text-[#0F172A]">Custom</span>
                        <p className="text-xs text-slate-400 mt-1">Pricing based on scope</p>
                      </div>
                    ) : (
                      <>
                        <span className="text-4xl font-bold text-[#0F172A] tabular-nums">
                          {plan.price === "Free" ? "Free" : (
                            billingAnnual ? `$${Math.round(parseInt(plan.price.replace("$", "")) * discount)}` : plan.price
                          )}
                        </span>
                        {plan.period && plan.price !== "Free" && (
                          <span className="text-sm text-slate-400 mb-1">{plan.period}</span>
                        )}
                      </>
                    )}
                  </div>
                  {billingAnnual && plan.price !== "Free" && !plan.customPricing && (
                    <p className="text-xs text-[#059669] font-medium">
                      Billed annually — save {Math.round((1 - discount) * 100)}%
                    </p>
                  )}
                </div>

                <ul className="space-y-2.5 mb-4 flex-1">
                  {plan.features.map(f => (
                    <li key={f} className="flex items-start gap-2 text-sm text-slate-600">
                      <CheckCircle2 size={14} className="text-[#059669] mt-0.5 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                  {plan.excluded.map(f => (
                    <li key={f} className="flex items-start gap-2 text-sm text-slate-400">
                      <span className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-center text-xs">—</span>
                      {f}
                    </li>
                  ))}
                </ul>

                {/* Micro UX note for custom plans */}
                {plan.customNote && (
                  <div className="mb-4 space-y-1">
                    {plan.customNote.map(note => (
                      <div key={note} className="flex items-center gap-1.5 text-[11px] text-slate-500">
                        <span className="text-[#059669]">✓</span> {note}
                      </div>
                    ))}
                  </div>
                )}

                <button
                  onClick={() => {
                    // Store tracking params
                    if (plan.trackingParams) {
                      sessionStorage.setItem("pricing_source", JSON.stringify(plan.trackingParams));
                    }
                    navigate(plan.ctaRoute || "/get-started");
                  }}
                  className={`w-full py-2.5 rounded-lg text-sm font-semibold transition-colors ${plan.ctaStyle}`}
                  data-testid={`plan-cta-${plan.key}`}
                >
                  {plan.cta} <ArrowRight size={13} className="inline ml-1" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Signals */}
      <section className="py-12 px-6 bg-[#F8FAFC] border-y border-slate-200">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 text-center">
            {[
              { icon: TrendingDown, label: "GHG Protocol Aligned", desc: "Methodology governance" },
              { icon: Shield, label: "Immutable Records", desc: "Audit-ready outputs" },
              { icon: Globe, label: "Public Registry", desc: "Verified credentials" },
              { icon: Award, label: "AI-Powered Insights", desc: "Claude Sonnet" },
            ].map(({ icon: Icon, label, desc }) => (
              <div key={label} className="flex flex-col items-center gap-2">
                <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center">
                  <Icon size={18} className="text-[#059669]" />
                </div>
                <p className="text-sm font-semibold text-slate-900">{label}</p>
                <p className="text-xs text-slate-400">{desc}</p>
              </div>
            ))}
          </div>
      </section>

      {/* Comparison Table */}
      <section className="py-16 px-6">
          <h2 className="text-2xl font-semibold text-[#0F172A] tracking-tight text-center mb-10">Compare plans</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 pr-6 font-medium text-slate-500 uppercase tracking-wider text-xs">Feature</th>
                  {plans.map(p => (
                    <th key={p.key} className={`py-3 px-4 text-center font-semibold text-xs uppercase tracking-wider ${p.highlight ? "text-[#059669]" : "text-slate-600"}`}>
                      {p.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {[
                  ["Assessments/year", "2", "Unlimited", "Unlimited", "Unlimited"],
                  ["Team members", "1", "5", "10", "Unlimited"],
                  ["Africa factor library (97 factors)", "—", "Yes", "Yes", "Yes"],
                  ["Climate Passport + Registry", "—", "Yes", "Yes", "Yes"],
                  ["AI Executive Insights", "—", "Yes", "Yes", "Yes"],
                  ["PDF Export (with charts)", "—", "Yes", "Yes", "Yes"],
                  ["Verified badge for website", "—", "Yes", "Yes", "Yes"],
                  ["Multi-asset dashboard", "—", "—", "Yes", "Yes"],
                  ["API access", "—", "—", "Yes", "Yes"],
                  ["DFI-ready reports", "—", "—", "—", "Yes"],
                  ["White-label reporting", "—", "—", "—", "Yes"],
                ].map(([feature, ...vals]) => (
                  <tr key={feature} className="hover:bg-slate-50">
                    <td className="py-3 pr-6 text-slate-700 font-medium">{feature}</td>
                    {vals.map((v, i) => (
                      <td key={i} className="py-3 px-4 text-center">
                        {v === "Yes" ? (
                          <CheckCircle2 size={16} className="mx-auto text-[#059669]" />
                        ) : v === "—" ? (
                          <span className="text-slate-300">—</span>
                        ) : (
                          <span className="text-slate-600">{v}</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-6 bg-[#F8FAFC] border-t border-slate-200">
          <h2 className="text-2xl font-semibold text-[#0F172A] tracking-tight text-center mb-10">Frequently asked questions</h2>
          <div className="space-y-3">
            {faq.map((item, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full text-left px-6 py-4 flex items-center justify-between gap-4">
                  <span className="text-sm font-semibold text-slate-900">{item.q}</span>
                  <span className="text-slate-400 flex-shrink-0">{openFaq === i ? "−" : "+"}</span>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-4 text-sm text-slate-600 leading-relaxed border-t border-slate-100 pt-3">
                    {item.a}
                  </div>
                )}
              </div>
            ))}
          </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 bg-[#0F172A] text-center">
        <h2 className="text-2xl font-semibold text-white mb-3">Ready to measure what matters?</h2>
        <p className="text-slate-400 mb-8 text-sm max-w-lg mx-auto">
          Join organizations across Africa building credible climate credentials with DeCarbonIQ.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/signup" className="inline-flex items-center gap-2 px-6 py-3 bg-[#059669] text-white font-medium rounded-lg hover:bg-emerald-700 text-sm">
            Start Free — No Credit Card <ArrowRight size={14} />
          </Link>
          <a href="mailto:info@decarboniq.com" className="inline-flex items-center gap-2 px-6 py-3 border border-slate-600 text-slate-300 font-medium rounded-lg hover:bg-slate-800 text-sm">
            Talk to Sales
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white py-6 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="bg-[#0F172A] rounded px-2 py-1">
              <img src={LOGO_URL} alt="DeCarbonIQ" className="h-4 w-auto" style={{ filter: "brightness(0) invert(1)" }} />
            </div>
            <span className="text-xs text-slate-400">Climate Intelligence Platform</span>
          </div>
          <p className="text-xs text-slate-400">© {new Date().getFullYear()} DeCarbonIQ</p>
        </div>
      </footer>
    </div>
  );
}
